import type { LoopState, PermanentState } from "../types"
import {
  getSeenSpiritScenes,
  getSpiritAudiencesRemaining,
  getSpiritQuestions,
  SPIRIT_AUDIENCES_PER_LOOP,
} from "../game/spiritEngine"
import { describeCurrentStrategy } from "../game/antiPlayerEngine"

interface SpiritScreenProps {
  permanent: PermanentState
  loop: LoopState
  onAskQuestion: (questionId: string) => void
  onReplayScene: (dialogueId: string) => void
  onBack: () => void
}

export function SpiritScreen({
  permanent,
  loop,
  onAskQuestion,
  onReplayScene,
  onBack,
}: SpiritScreenProps) {
  const questions = getSpiritQuestions(permanent)
  const seenScenes = getSeenSpiritScenes(permanent)
  const audiencesRemaining = getSpiritAudiencesRemaining(loop)
  const anti = permanent.antiPlayerState

  return (
    <div className="screen spirit-screen">
      <h1>The Good Spirit</h1>
      <p>
        It is not in the room so much as adjacent to it. What it says depends
        entirely on what you have found out for yourself — it does not seem able
        to volunteer anything you have not already half-earned.
      </p>

      <dl className="status-line">
        <dt>Scenes Witnessed</dt>
        <dd>{permanent.spiritProgress}</dd>
        <dt>Questions Answered</dt>
        <dd>{permanent.spiritQuestionsAsked.length}</dd>
        <dt>Audiences Left This Loop</dt>
        <dd>
          {audiencesRemaining} / {SPIRIT_AUDIENCES_PER_LOOP}
        </dd>
      </dl>

      <h2>Ask a Question</h2>
      {/* New questions cost one of the loop's audiences; an answer already
          earned is re-readable forever and free, so a player who died with
          knowledge in hand never has to buy it twice. */}
      <p className="spirit-note">
        The spirit will take up {SPIRIT_AUDIENCES_PER_LOOP} new questions in a
        loop. Answers you have already earned can be re-read as often as you
        like, in this loop or any other.
      </p>
      <ul className="spirit-question-list">
        {questions.map(({ question, unlocked, asked }) => {
          const affordable = asked || audiencesRemaining > 0
          return (
            <li key={question.id}>
              {unlocked ? (
                <button
                  type="button"
                  disabled={!affordable}
                  onClick={() => onAskQuestion(question.id)}
                >
                  {question.question}
                  {asked
                    ? " (answered — re-read freely)"
                    : affordable
                      ? ""
                      : " (no audiences left this loop)"}
                </button>
              ) : (
                <p className="training-locked">
                  {question.question} — {question.lockedHint}
                </p>
              )}
            </li>
          )
        })}
      </ul>

      {seenScenes.length > 0 && (
        <>
          <h2>What It Has Told You</h2>
          <ul className="spirit-scene-list">
            {seenScenes.map((scene) => (
              <li key={scene.id}>
                <button type="button" onClick={() => onReplayScene(scene.dialogueId)}>
                  {scene.title}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {anti.encounterCount > 0 && (
        <>
          <h2>The Other Who Remembers</h2>
          <dl className="status-line">
            <dt>Times Met</dt>
            <dd>{anti.encounterCount}</dd>
            <dt>Times Beaten</dt>
            <dd>{anti.defeatCount}</dd>
            <dt>Name</dt>
            <dd>{anti.identityDiscovered ? "Aldric Vane" : "Unknown"}</dd>
            <dt>What He Wants</dt>
            <dd>
              {anti.motivationDiscovered
                ? "To close the loop by removing the one who holds it open — as he understands it, you."
                : "Unknown"}
            </dd>
            <dt>Methods He Has Abandoned</dt>
            <dd>
              {anti.failedMethods.length > 0
                ? anti.failedMethods.join(", ")
                : "None yet — he has not had to change anything."}
            </dd>
          </dl>
          <p className="spirit-note">{describeCurrentStrategy(permanent)}</p>
        </>
      )}

      <button type="button" onClick={onBack}>
        Back to Map
      </button>
    </div>
  )
}
